import { createReducer, on } from '@ngrx/store';
import { FolderContent, IFolder, INewsArticle, IReport } from 'src/app/core/models/resource';
import { SchoolLibrary } from '../../models/library';
import { resetAppState } from '../global/global.actions';
import { LibraryActions, ResourceActions } from './resource.actions';

export const resourcesFeatureKey = 'resources';

export interface ResourceState {
  // Library / folder state
  folders: Record<string, IFolder[]>; // keyed by schoolCode
  folderResources: Record<string, FolderContent[]>; // keyed by folderId
  selectedSchoolCode: string | null;
  selectedFolderId: string | null;
  foldersLoading: boolean;
  foldersError: unknown;
  folderResourcesLoading: boolean;
  folderResourcesError: unknown;

  // News (separate tracking)
  news: INewsArticle[];
  newsLoading: boolean;
  newsError: unknown;

  // Reports (separate tracking)
  reports: Record<string, IReport[]>;
  reportsLoading: boolean;
  reportsError: unknown;
}

export const initialResourceState: ResourceState = {
  folders: {},
  folderResources: {},
  selectedSchoolCode: null,
  selectedFolderId: null,
  foldersLoading: false,
  foldersError: null,
  folderResourcesLoading: false,
  folderResourcesError: null,

  news: [],
  newsLoading: false,
  newsError: null,

  reports: {},
  reportsLoading: false,
  reportsError: null,
};

export const resourceReducer = createReducer(
  initialResourceState,

  // School selection
  on(ResourceActions.selectSchool, (state, { schoolCode }) => ({
    ...state,
    selectedSchoolCode: schoolCode,
    selectedFolderId: null,
  })),

  // Folders
  on(ResourceActions.loadFolders, (state) => ({
    ...state,
    foldersLoading: true,
    foldersError: null,
  })),
  on(ResourceActions.loadFoldersSuccess, (state, { schoolCode, folders }) => ({
    ...state,
    folders: { ...state.folders, [schoolCode]: folders },
    foldersLoading: false,
  })),
  on(ResourceActions.loadFoldersFailure, (state, { error }) => ({
    ...state,
    foldersLoading: false,
    foldersError: error,
  })),

  // Folder selection
  on(ResourceActions.selectFolder, (state, { folderId }) => ({
    ...state,
    selectedFolderId: folderId,
  })),

  // Folder resources
  on(ResourceActions.loadFolderResources, (state) => ({
    ...state,
    folderResourcesLoading: true,
    folderResourcesError: null,
  })),
  on(ResourceActions.loadFolderResourcesSuccess, (state, { folderId, resources }) => ({
    ...state,
    folderResources: { ...state.folderResources, [folderId]: resources },
    folderResourcesLoading: false,
  })),
  on(ResourceActions.loadFolderResourcesFailure, (state, { error }) => ({
    ...state,
    folderResourcesLoading: false,
    folderResourcesError: error,
  })),

  // News
  on(ResourceActions.loadNewsArticles, (state) => ({
    ...state,
    newsLoading: true,
    newsError: null,
  })),
  on(ResourceActions.loadNewsArticlesSuccess, (state, { articles }) => ({
    ...state,
    news: articles,
    newsLoading: false,
  })),
  on(ResourceActions.loadNewsArticlesFailure, (state, { error }) => ({
    ...state,
    newsLoading: false,
    newsError: error,
  })),

  // Reports
  on(ResourceActions.loadStudentReports, (state) => ({
    ...state,
    reportsLoading: true,
    reportsError: null,
  })),
  on(ResourceActions.loadReportsSuccess, (state, { reports }) => ({
    ...state,
    reports: { ...state.reports, ...reports },
    reportsLoading: false,
  })),
  on(ResourceActions.loadReportsFailure, (state, { error }) => ({
    ...state,
    reportsLoading: false,
    reportsError: error,
  })),

  on(resetAppState, () => initialResourceState),
);

//==============================================================================================================================
export interface LibraryState {
  libraries: SchoolLibrary[];
  isLoading: boolean;
  activeError: string | null;
  isNotFound: boolean;
}

export const initialLibraryState: LibraryState = {
libraries: [],
  isLoading: false,
  activeError: null,
  isNotFound: false
};

export const libraryReducer = createReducer(
  initialLibraryState,

  // Hydrate the state array with the complete payload
  on(LibraryActions.hydrateLibraries, (state, { libraries }) => ({
    ...state,
    libraries: [...libraries],
    isLoading: false
  })),

  // Reset the state configuration clean
  on(LibraryActions.clearLibraries, () => initialLibraryState),
  on(LibraryActions.loadSchoolLibraryFailure, (state, { error, statusCode }) => ({
  ...state,
  loading: false,
  activeError: error,
  isNotFound: statusCode === 404
}))
);
